import { Component, Input, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { RequestStatus } from '../../../../../../model/enum/request.status.enum';
import { Partner } from '../../../../../models/partner.model';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ReviewSidebarComponent } from '../../../review-sidebar/review-sidebar.component';
import { Utils } from '../../../../../utils/utils';
import { ScreenSizeService } from '../../../../../../services/utils/screenSize.service';
import { OpenWriteReviewModalService } from '../../../../../../services/openWriteReviewModal.service';
import { LocalizedRouterService } from '../../../../../localized-router.service';
import { TranslateService } from '@ngx-translate/core';
import { ClinicDetailOffcanvasComponent } from '../../../clinic-detail/clinic-detail-modal.component';
import { OffcanvasHistoryService } from '../../../../../../services/offcanvas-history.service';
import { ViewStatus } from '@uranda/ui';

@Component({
  selector: 'app-clinic-list-item-compact',
  templateUrl: './clinic-list-item-compact.component.html',
  styleUrls: ['./clinic-list-item-compact.component.scss'],
  standalone: false
})
export class ClinicListItemCompactComponent {
  rating = 3.5;
  averageRating = 8.5;
  reviewCount = 132;
  isLong: boolean = false;
  @Input() noBorderTop = false;
  @Input() noBorderBottom = false;
  @Input() viewStatus: ViewStatus = ViewStatus.LOADING;

  constructor(
    private router: Router,
    private localizedRouter: LocalizedRouterService,
    protected screenSize: ScreenSizeService,
    private offcanvasService: NgbOffcanvas,
    private offcanvasHistory: OffcanvasHistoryService,
    private openWriteReviewModalService: OpenWriteReviewModalService,
    private translateService: TranslateService
  ) { }

  animationState: 'hidden' | 'visible' = 'hidden';
  @Input() partner?: Partner;
  @Input() flags?: string[] = [];
  flagsSrcs?: { src: string; name: string }[];
  coverImageLoaded = false;
  coverImageMobileLoaded = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['partner'] && this.partner) {
      this.coverImageLoaded = false;
      this.coverImageMobileLoaded = false;
    }
  }

  onImageLoad() {
    this.coverImageLoaded = true;
  }

  onMobileImageLoad() {
    this.coverImageMobileLoaded = true;
  }

  onImageError(event: Event, isMobile: boolean = false) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/dentalclinic.webp';
    if (isMobile) {
      this.coverImageMobileLoaded = true;
    } else {
      this.coverImageLoaded = true;
    }
  }


  ngOnInit() {
    setTimeout(() => {
      this.animationState = 'visible';
      this.isLongName();
    }, 100);

    if (this.flags) {
      this.flagsSrcs = this.flags.map((code) => ({
        src: Utils.getFlagSrc(code),
        name: Utils.getFlagName(code),
      }));
    }
  }

  async openReviewsOffcanvas(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    try {

      const offcanvasRef = this.offcanvasHistory.open(
        ReviewSidebarComponent,
        {
          position: 'end',
          scroll: false,
          backdrop: true,
          panelClass: 'reviews-offcanvas-panel',
        }
      );

      offcanvasRef.componentInstance.partnerId = this.partner?.id;
      offcanvasRef.componentInstance.clinicName = this.partner?.name;
      offcanvasRef.componentInstance.averageRating = this.partner?.averageRate;
      offcanvasRef.componentInstance.totalReviews = this.partner?.reviewsCount;
      offcanvasRef.componentInstance.viewStatus = this.viewStatus;
      

      offcanvasRef.result.then(() => {
        document.body.style.overflow = 'auto';
      })

    } catch (error) { }
  }

  openDetailsModal(partner: Partner) {
    const modalRef = this.offcanvasHistory.open(ClinicDetailOffcanvasComponent, {
      position: 'end',
      scroll: false,
      backdrop: 'static',
      panelClass: 'clinic-details-offcanvas-panel',
    });
    modalRef.componentInstance.partner = partner;
    modalRef.componentInstance.isOffcanvas = true;
    modalRef.result.then(() => {
      document.body.style.overflow = 'auto';
    });
  }

  async openWriteReviewModal() {
    if (!this.partner) return;

    const partnerInfo = {
      id: this.partner.id,
      name: this.partner.name,
    };

    this.openWriteReviewModalService
      .openWriteReviewModal(partnerInfo)
      .then((result) => {
        if (result && result.success) {
        }
      })
      .catch((dismissReason) => {
        console.log('Review modal dismissed:', dismissReason);
      });
  }

  get showReviewUnder(): boolean {
    return (
      (this.partner ? this.partner?.name?.length > 13 : false) &&
      this.screenSize.isMediumDesktop
    );
  }

  async navigateToDetail() {
    if (!this.partner?.id) return;

    const lang = this.localizedRouter.getCurrentLang();
    const segments = [lang, 'partner', this.partner.id];

    if (this.screenSize.isMobile) {
      this.openDetailsModal(this.partner);
    } else {
      const url = this.router.serializeUrl(this.router.createUrlTree(segments));
      window.open(url, '_blank');
    }
  }

  protected readonly RequestStatus = RequestStatus;

  get fullStars() {
    return Array(Math.floor(this.rating));
  }

  get hasHalfStar() {
    return this.rating % 1 >= 0.5;
  }

  get emptyStars() {
    return Array(5 - Math.ceil(this.rating));
  }

  isLongName() {
    if (this.partner) {
      if (
        this.partner.name &&
        this.screenSize.isDesktop &&
        this.screenSize.isMediumDesktop
      ) {
        this.isLong = this.partner.name.length > 13;
      } else {
        this.isLong = false;
      }
    }
  }

  get hasReviews(): boolean {
    return (this.partner?.reviewsCount ?? 0) > 0;
  }

  get reviewsLabel(): string {
    const count = this.partner?.reviewsCount ?? 0;
    const review = count === 1 ? 'review' : 'reviews';
    return `${count} ${this.translateService.instant(review)}`;
  }
}